import axios from "../utils/axios";

/**
 * @typedef {{
 *  familiesInScope: number;
 *  familiesActiveAtStart: number;
 *  reactivated: number;
 *  inviteQueued: number;
 *  inviteSent: number;
 *  inviteRemaining: number;
 *  stillMissing: number;
 *  reminders: Array<{
 *    id: number; round: number; messageName: string;
 *    queued: number; sent: number; remaining: number;
 *  }>;
 * }} RecertificationProgress
 *
 * @typedef {{
 *  id: number;
 *  masbiaLocationId: number;
 *  locationName: string;
 *  name: string;
 *  startedOn: string;
 *  endDate: string;
 *  status: number;
 *  statusName: "Open" | "Closed";
 *  excludedExceptions?: string;
 *  isTest: boolean;
 *  batchSize: number;
 *  inviteMessageId?: number;
 *  inviteMessageName?: string;
 *  closedOn?: string;
 *  createdBy: string;
 *  daysRemaining?: number;
 *  progress: RecertificationProgress;
 * }} RecertificationCycle
 */

/** @returns {Promise<Array<RecertificationCycle>>} */
export function getCycles() {
  return axios.get("/Recertification/GetAll");
}

/** @returns {Promise<RecertificationCycle>} */
export function getCycle(id) {
  return axios.get(`/Recertification/Get/${id}`);
}

/**
 * Family counts and the exception breakdown for a location, so the start screen can show who is
 * about to be deactivated before anyone commits.
 */
export function getLocationPreview(locationId) {
  return axios.get(`/Recertification/LocationPreview/${locationId}`);
}

/** Starts the drive immediately. Deactivates the families in scope. */
export function startRecertification(data) {
  return axios.post("/Recertification/Start", data);
}

/** Queues the next batch of opening messages. */
export function sendBatch(id) {
  return axios.post(`/Recertification/SendBatch/${id}`);
}

/** Queues the next batch of one reminder round, to families who have not come back yet. */
export function sendReminderBatch(id, reminderId) {
  return axios.post(`/Recertification/SendReminderBatch/${id}/${reminderId}`);
}

/** Adds another round of chasing, with its own message. */
export function addReminder(id, messageId) {
  return axios.post(`/Recertification/AddReminder/${id}/${messageId}`);
}

export function updateSettings(id, { endDate, batchSize }) {
  return axios.post(`/Recertification/UpdateSettings/${id}`, {
    endDate,
    batchSize,
  });
}

export function closeCycle(id) {
  return axios.post(`/Recertification/Close/${id}`);
}

export function exportCycle(id, onlyNotRecertified = true) {
  return axios.get(
    `/Recertification/Export/${id}?onlyNotRecertified=${onlyNotRecertified}`,
    { responseType: "blob" }
  );
}
